import { type ProfileProjects_userProfile$key } from '#app/__generated__/relay/ProfileProjects_userProfile.graphql.js';
import { type ProfileProjectsReorderMutation } from '#app/__generated__/relay/ProfileProjectsReorderMutation.graphql.js';
import { type BlogCategories } from '#app/components/BlogCategoriesInput/types.js';
import { BlogCategoriesList } from '#app/components/BlogCategoriesList/BlogCategoriesList.js';
import { getCategoryHref } from '#app/components/CategoryTag/util.js';
import { ProfileMagicBannerCTA } from '#app/components/ContraMagic/ProfileMagicBannerCTA/ProfileMagicBannerCTA.js';
import { Link } from '#app/components/Link/index.js';
import { Menu } from '#app/components/Menu/Menu.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useSortableListView } from '#app/components/SortableListView/hooks/useSortableListView.js';
import { SortableListItemWithClonedElement } from '#app/components/SortableListView/SortableListItemWithClonedElement.js';
import { SortableListView } from '#app/components/SortableListView/SortableListView.js';
import { ProjectCardV2DataWrapper } from '#app/components/UserProfile/ProjectCardV2/ProjectCardV2DataWrapper.js';
import { CATEGORY_SLUG_MAP } from '#app/constants/blog.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useRouteParameter } from '#app/hooks/useRouteParameter.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { formatName } from '#app/utilities/formatName.js';
import { isBlogCategorySlug } from '#app/utilities/isBlogCategorySlug.js';
import { BlankCardMessage } from '../BlankCardMessage/BlankCardMessage.js';
import { BlogCard } from '../BlogCard/BlogCard.js';
import { CreateCard } from '../CreateCard/CreateCard.js';
import { FilterIcon } from '@contra/icons/icons';
import { useToggle } from '@contra/react-hooks/useToggle';
// disable useContraMutation eslint error
import { graphql, useFragment, useMutation } from '@contra/relay';
import { type UniqueIdentifier } from '@dnd-kit/core';
import { type MouseEvent, useCallback, useMemo } from 'react';
import { css, styled } from 'styled-components';

type ProfileProjectsProps = {
  readonly blogCategories: BlogCategories;
  readonly profileKey: ProfileProjects_userProfile$key;
  readonly showLinkedProjectsModal?: (event: MouseEvent) => void;
};

const MenuItem = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  padding: 8px 0;

  & > *:not(:last-child) {
    margin-right: 8px;
  }
`;

const FilterPanelButton = styled.button`
  ${({ theme }) => css`
    width: 100%;
    border: none;
    padding: 32px 0 24px 0;
    color: ${theme.colorsV2.gray70};
    font-size: ${theme.textStyles.subtitleSmall.fontSize};
    font-weight: 600;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;

    & > svg {
      margin-right: 4px;
    }
  `}
`;

const CategoryLabel = styled(Text).attrs({ textStyle: 'subtitleRegular' })`
  margin-top: 8px;
`;

const FilterHeader = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-flow: column nowrap;
    margin-top: 24px;
    margin-bottom: 32px;

    ${theme.mediaQueries.md} {
      flex-flow: row nowrap;

      & > *:not(:last-child) {
        margin-right: 44px;
      }
    }
  `}
`;

const CategoryTags = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-flow: row wrap;
    flex-grow: 1;
    border-bottom: 1px solid ${theme.colorsV2.gray30};
    justify-content: center;
    padding-bottom: 8px;

    & > * {
      margin-top: 8px;

      &:not(:last-child) {
        margin-right: 8px;
      }
    }

    ${theme.mediaQueries.md} {
      border: none;
      justify-content: start;
    }
  `}
`;

const ProjectCards = styled.div<{
  withMarginTop: boolean;
}>`
  ${({ theme, withMarginTop }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;

    & > * + * {
      margin-top: 32px;

      ${theme.mediaQueries.md} {
        margin-top: 40px;
      }
    }

    ${withMarginTop
      ? css`
          margin-top: 40px;
        `
      : undefined}
    // we add extra margin-bottom here on mobile to account for the profile checklist button overlapping the last project card
    margin-bottom: 64px;

    ${theme.mediaQueries.md} {
      margin-bottom: unset;
    }
  `}
`;

export const ProfileProjects = ({
  blogCategories,
  profileKey,
  showLinkedProjectsModal,
}: ProfileProjectsProps) => {
  const profile = useFragment<ProfileProjects_userProfile$key>(
    graphql`
      fragment ProfileProjects_userProfile on UserProfile {
        id
        visitorCanEdit
        firstName
        profileDisplayType
        displayUsername
        portfolioProjects(type: ALL) {
          count
          edges {
            node {
              id
              author {
                id
              }
              blogCategories {
                edges {
                  node {
                    id
                  }
                }
              }
              ...ProjectCardV2DataWrapper_portfolioProject
              ...BlogCard_portfolioProject
            }
          }
        }
        ...ProjectCardV2DataWrapper_userProfile
        ...BlogCard_userProfile
      }
    `,
    profileKey,
  );

  const [updateProjectOrder] = useMutation<ProfileProjectsReorderMutation>(
    graphql`
      mutation ProfileProjectsReorderMutation(
        $input: ReorderPortfolioProjectsInput!
      ) {
        reorderPortfolioProjects(input: $input) {
          errors {
            message
          }
          userProfile {
            ...ProfileProjects_userProfile
          }
        }
      }
    `,
  );

  const onProjectIdsSorted = useCallback(
    (ids: UniqueIdentifier[]) => {
      updateProjectOrder({
        variables: {
          input: {
            portfolioProjects: ids.map((id) => id.toString()),
          },
        },
      });
    },
    [updateProjectOrder],
  );

  const categorySlug = useRouteParameter('categorySlug');
  const currentCategory = useMemo(() => {
    if (categorySlug && isBlogCategorySlug(categorySlug)) {
      return CATEGORY_SLUG_MAP[categorySlug];
    }

    return null;
  }, [categorySlug]);

  const [isFiltersOpen, toggleFilters] = useToggle(false);
  const { selectedUserType } = useSelectedUserType();
  const isMobile = useIsMobile();

  const isBlogProfile = profile.profileDisplayType === 'BLOG';

  const projects = useMemo(() => {
    const filteredProjects = profile.portfolioProjects.edges.map(
      (edge) => edge.node,
    );

    if (isBlogProfile && currentCategory) {
      /**
       * If we have a category filter on a blog page, we want to filter Projects by their
       * blogCategories to ensure each project/blog post is in the category that the
       * user has filtered down to.
       */
      return currentCategory
        ? filteredProjects.filter(
            ({ blogCategories: currentProjectCategories }) => {
              // Grab all category IDs for this project/blog post
              const categoryIds =
                currentProjectCategories?.edges.map(
                  ({ node: { id: projectCategoryId } }) => projectCategoryId,
                ) ?? [];
              // Ensure at least one of them is in the selectedCategories.
              // As a side note, I feel like (Boolean(.find)) would be more performant here
              // but eslint rules disallow that
              return categoryIds.includes(currentCategory);
            },
          )
        : filteredProjects;
    }

    return filteredProjects;
  }, [profile.portfolioProjects.edges, isBlogProfile, currentCategory]);

  const hasProjects = projects.length > 0;

  // evaluate conditions when we should disable sorting.
  const shouldDisableSorting =
    // use has edit permissions
    !profile.visitorCanEdit ||
    // we have less than 2 projects to disable
    projects.length < 2;

  const sortState = useSortableListView({
    data: projects,
    disabled: shouldDisableSorting,
    onDataIdsSorted: onProjectIdsSorted,
  });

  const {
    activeSortId,
    disabled,
    sortedData,
    sortedNodeIds,
    ...sortListProps
  } = sortState;

  const handleCreateProjectClick = useCallback(
    (event: MouseEvent) => {
      showLinkedProjectsModal?.(event);
    },
    [showLinkedProjectsModal],
  );

  if (!sortedData || !sortedNodeIds) {
    return null;
  }

  const blogCategoriesWithAll = [{ id: 'all', name: 'All' }, ...blogCategories];

  return (
    <>
      {isBlogProfile ? (
        <>
          {isMobile ? (
            <Menu
              isOpen={isFiltersOpen}
              toggleMenu={toggleFilters}
            >
              <Menu.Reference>
                <FilterPanelButton
                  aria-label="open filter panel"
                  onClick={() => toggleFilters()}
                  type="button"
                >
                  <FilterIcon />
                  Filters
                </FilterPanelButton>
              </Menu.Reference>
              <Menu.Panel>
                <CategoryLabel>Category</CategoryLabel>
                {blogCategoriesWithAll
                  .filter(
                    ({ name }) =>
                      ![
                        'contra for clients',
                        'contra for independents',
                      ].includes(name.trim().toLowerCase()),
                  )
                  .map(({ id: categoryKey, name }) => (
                    <MenuItem
                      key={categoryKey}
                      onClick={() => toggleFilters()}
                    >
                      <Link
                        to={getCategoryHref({
                          displayUsername: profile.displayUsername,
                          id: categoryKey,
                          name,
                        })}
                      >
                        <Text
                          as="label"
                          color="uiBlackHighEmphasis"
                          htmlFor={categoryKey}
                          textStyle="bodyRegular"
                        >
                          {name}
                        </Text>
                      </Link>
                    </MenuItem>
                  ))}
              </Menu.Panel>
            </Menu>
          ) : (
            <FilterHeader>
              <CategoryTags>
                <BlogCategoriesList
                  asLinks
                  categories={blogCategoriesWithAll}
                  displayUsername={profile.displayUsername}
                  hideGroupingCategories
                  selectedCategory={currentCategory}
                />
              </CategoryTags>
            </FilterHeader>
          )}
        </>
      ) : null}

      {!hasProjects && !profile.visitorCanEdit ? (
        <BlankCardMessage>
          {isBlogProfile ? (
            <>No posts found! </>
          ) : (
            <>
              {formatName({ firstName: profile.firstName }, 'firstName')} hasn't
              added any work yet
            </>
          )}
        </BlankCardMessage>
      ) : null}

      {profile.visitorCanEdit && profile?.portfolioProjects?.count === 0 ? (
        <ProfileMagicBannerCTA
          displayUsername={profile.displayUsername}
          variant="project"
          visitorCanEdit={profile.visitorCanEdit}
        />
      ) : null}

      {profile.visitorCanEdit ? (
        <CreateCard
          onCreateClick={handleCreateProjectClick}
          variant={selectedUserType === 'CLIENT' ? 'client-work' : 'project'}
        />
      ) : null}
      <ProjectCards withMarginTop={profile.visitorCanEdit}>
        <SortableListView
          sortedNodeIds={sortedNodeIds}
          {...sortListProps}
        >
          {sortedData.map((node, index) => {
            return (
              <SortableListItemWithClonedElement
                activeSortId={activeSortId}
                disabled={disabled}
                key={node.id}
                sortId={node.id}
              >
                {/* Sortable list while clone its child elements
                to add some props that are specific to sorting. */}
                {node.author ? (
                  <BlogCard
                    canEdit={profile.visitorCanEdit}
                    key={node.id}
                    portfolioProject={node}
                    position={index}
                    userProfileRef={profile}
                  />
                ) : (
                  <ProjectCardV2DataWrapper
                    canEdit={profile.visitorCanEdit}
                    isLastElement={index === projects.length - 1}
                    key={node.id}
                    portfolioProject={node}
                    position={index}
                    userProfileRef={profile}
                  />
                )}
              </SortableListItemWithClonedElement>
            );
          })}
        </SortableListView>
      </ProjectCards>
    </>
  );
};
